// composite.result-card.v1
//
// Generic "output / result / completed item" card. Used by Generative
// Studio (each gen job), CRM (each record
// row when displayed as cards).
//
// Slots: media (image/preview), title, subtitle, meta (chips/badges),
// description, actions. Drop unused slots and they collapse.

import { type JSX, Show, splitProps } from "solid-js"

export interface ResultCardProps {
  media?: JSX.Element
  title: JSX.Element
  subtitle?: JSX.Element
  meta?: JSX.Element // typically chips/status indicators
  description?: JSX.Element
  actions?: JSX.Element
  href?: string
  onClick?: (event: MouseEvent) => void
  class?: string
}

export function ResultCard(props: ResultCardProps) {
  const [local, rest] = splitProps(props, [
    "media",
    "title",
    "subtitle",
    "meta",
    "description",
    "actions",
    "href",
    "onClick",
    "class",
  ])

  const interactive = !!local.href || !!local.onClick

  return (
    <div
      data-registry-id="composite.result-card"
      data-interactive={interactive ? "true" : undefined}
      onClick={local.onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      classList={{
        "flex flex-col gap-3 rounded-xl border border-border-weak-base bg-background-base p-4 min-w-0": true,
        "transition-colors duration-150 hover:border-border-strong-base hover:bg-surface-raised-base cursor-pointer":
          interactive,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <Show when={local.media}>
        <div data-slot="result-card-media" class="rounded-lg overflow-hidden">
          {local.media}
        </div>
      </Show>

      <div class="flex flex-col gap-1 min-w-0">
        <div class="text-15-medium text-text-strong truncate">{local.title}</div>
        <Show when={local.subtitle}>
          <div class="text-13-regular text-text-base truncate">{local.subtitle}</div>
        </Show>
      </div>

      <Show when={local.meta}>
        <div data-slot="result-card-meta" class="flex flex-wrap items-center gap-1.5">
          {local.meta}
        </div>
      </Show>

      <Show when={local.description}>
        <div class="text-13-regular text-text-base">{local.description}</div>
      </Show>

      <Show when={local.actions}>
        <div data-slot="result-card-actions" class="flex items-center gap-2 mt-1">
          {local.actions}
        </div>
      </Show>
    </div>
  )
}
